import React, { forwardRef, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import deLocale from 'date-fns/locale/de'
import frLocale from 'date-fns/locale/fr'
import itLocale from 'date-fns/locale/it'
import enLocale from 'date-fns/locale/en-GB'
import esLocale from 'date-fns/locale/es'
import usLocale from 'date-fns/locale/en-US'
import TextField from '../TextField'
import RfButton from '../Rf-button'

import './Datepicker.scss'

const localeMap = {
	de: deLocale,
	fr: frLocale,
	it: itLocale,
	en: enLocale,
	es: esLocale,
	us: usLocale
}

const maskMap = {
	de: '__.__.____',
	fr: '__/__/____',
	it: '__/__/____',
	en: '__/__/____',
	es: '__/__/____',
	us: '__/__/____'
}

const Datepicker = forwardRef(function Datepicker(props, ref) {
	const {
		className,
		defaultValue = null,
		disabled,
		helperText,
		isCorrect: isCorrectProp,
		label,
		locale = 'en',
		onChange,
		onError,
		onAccept,
		shouldDisableDate
	} = props

	const [open, setOpen] = useState(false)
	const [opened, setOpened] = useState(false)
	const [isCorrect, setIsCorrect] = useState(null)
	const [errorReason, setErrorReason] = useState(null)
	const [value, setValue] = useState(defaultValue)
	const [iconColor, setIconColor] = useState(disabled ? 'gray-3' : 'primary-lima')

	const openPickerButtonRef = useRef(null)

	useEffect(() => {
		if (open) {
			setTimeout(() => {
				setOpened(open)
			}, 100)
		} else {
			setOpened(false)
		}
	}, [open])

	useEffect(() => {
		if (errorReason || isCorrectProp === false) {
			setIsCorrect(false)
			setIconColor(disabled ? 'gray-3' : 'error')
		} else {
			setIsCorrect(true)
			setIconColor(disabled ? 'gray-3' : 'primary-lima')
		}
	}, [errorReason, isCorrectProp])

	const handleChange = (newValue) => {
		setValue(newValue)
		typeof onChange === 'function' && onChange(newValue)
		setOpen(false)
	}

	const handleError = (reason) => {
		setErrorReason(reason)
		typeof onError === 'function' && onError(reason)
	}

	const handleAccept = (newValue) => {
		setIsCorrect(true)
		setIconColor(disabled ? 'gray-3' : 'primary-lima')
		typeof onAccept === 'function' && onAccept(newValue)
	}

	const handleToggle = () => {
		if (!disabled) {
			setOpen(!open)
		}
	}

	const handleClose = () => {
		setOpen(false)
	}

	const handleCloseWhenClickOutside = (e) => {
		if (
			open &&
			popperRef.current &&
			!popperRef.current.contains(e.target) &&
			!openPickerButtonRef.current.contains(e.target)
		) {
			handleClose()
		}
	}

	useEffect(() => {
		document.addEventListener('click', handleCloseWhenClickOutside)

		return () => {
			document.removeEventListener('click', handleCloseWhenClickOutside)
		}
	}, [open])

	const popperRef = useRef(null)

	return (
		<div className={clsx('Vb-datepicker', open && 'Vb-datepicker--opened', className)}>
			<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[locale] || enLocale}>
				<DesktopDatePicker
					ref={ref}
					label={label}
					onChange={handleChange}
					onError={handleError}
					onAccept={handleAccept}
					disabled={disabled}
					// Uncomment this prop if datepicker will be used as Dialog-mode in mobile
					// DialogProps={{
					// 	onBackdropClick: handleClose()
					// }}
					components={{
						LeftArrowButton: function LeftArrowButton(allProps) {
							const { children, size, ...props } = allProps
							return <RfButton icon="arrow-left" {...props} />
						},
						RightArrowButton: function RightArrowButton(allProps) {
							const { children, size, ...props } = allProps
							return <RfButton icon="arrow-right" {...props} label={null} />
						}
					}}
					maskMap={maskMap[locale]}
					value={value}
					reduceAnimations
					shouldDisableDate={shouldDisableDate}
					PopperProps={{
						className: clsx('Vb-datepicker__popper', opened && 'Vb-datepicker__popper--opened'),
						ref: popperRef,
						placement: 'bottom-start'
					}}
					views={['day']}
					open={open}
					renderInput={({ inputRef, inputProps }) => (
						<div className="Vb-datepicker__input-wrapper">
							<TextField
								inputRef={inputRef}
								{...inputProps}
								label={label}
								isCorrect={isCorrect}
								helperText={helperText}
								disabled={disabled}
							/>
							<RfButton
								ref={openPickerButtonRef}
								className="Vb-datepicker__open-picker-button"
								onClick={handleToggle}
								icon="calendar"
								IconProps={{ size: 1.1, color: iconColor }}
								variant="naked"
								color="dark"
								disabled={disabled}
							/>
						</div>
					)}
				/>
			</LocalizationProvider>
		</div>
	)
})

export default Datepicker

Datepicker.defaultProps = {
	defaultValue: null,
	locale: 'en'
}

Datepicker.propTypes = {
	/**
	 * className applied to the root component.
	 */
	className: PropTypes.string,
	/**
	 * The value of the picker.
	 * @default null
	 */
	defaultValue: PropTypes.oneOfType([PropTypes.any, PropTypes.instanceOf(Date), PropTypes.number, PropTypes.string]),
	/**
	 * If `true`, the picker and text field are disabled.
	 */
	disabled: PropTypes.bool,
	/**
	 * The text of the HelperText element,
	 * that displays a helper text below the TextField.
	 */
	helperText: PropTypes.string,
	/**
	 * Flag to store the validation of the input
	 * and it's resulting styling.
	 */
	isCorrect: PropTypes.bool,
	/**
	 * The label associated to the input.
	 */
	label: PropTypes.node,
	/**
	 * The language code of the datepicker.
	 * @default 'en'
	 */
	locale: PropTypes.string,
	/**
	 * Callback fired when the value (the selected date) changes.
	 */
	onChange: PropTypes.func,
	/**
	 * Callback that fired when input value or new `value` prop validation returns **new** validation error (or value is valid after error).
	 */
	onError: PropTypes.func
}
